import { create } from 'zustand';
import { getPublicSettings } from '../services/publicSettingsApi';
import type { PublicDefaults } from '../services/publicSettingsApi';

// Process-wide feature-flag + platform-defaults state, loaded at app boot from
// GET /settings/public. Components gate UI with useFeature('ai_explain').
//
// Fails OPEN for feature FLAGS (unknown/unloaded -> treated as enabled) so a
// transient boot error never hides core UI; the backend gates the actual calls
// and fails CLOSED, which is the real security boundary.
//
// DEFAULTS ARE DIFFERENT, and 2026-09-24 is why. A failed fetch used to leave
// `defaults` empty, and `defaults.access_code_enabled` going from true to
// undefined silently removed a sign-in option from the login page. Mid-class,
// with the campus sharing one rate-limit budget, the tab appeared and vanished
// on refresh while the setting was `true` in the database the whole time.
//
// So: the last settings that actually arrived are remembered on this device
// and used when a fetch fails, and a failure is retried rather than accepted.
// A network blip must never change what the UI believes the platform is
// configured to do.

const CACHE_KEY = 'ace.publicsettings.v1';

interface Cached {
  features: Record<string, boolean>;
  defaults: PublicDefaults;
}

function readCache(): Cached | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c || typeof c !== 'object') return null;
    return { features: c.features || {}, defaults: c.defaults || {} };
  } catch {
    return null;
  }
}

function writeCache(c: Cached): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(c));
  } catch {
    /* private window or blocked storage — this session still works */
  }
}

/** Retry schedule for a failed boot fetch, in ms. Short, then backing off. */
const RETRY_MS = [2000, 5000, 15000, 30000];

interface FeatureState {
  features: Record<string, boolean>;
  defaults: PublicDefaults;
  loaded: boolean;
  /** True when what we are showing came from cache, not from the server. */
  stale: boolean;
  load: () => Promise<void>;
  isEnabled: (key: string) => boolean;
}

export const useFeatureStore = create<FeatureState>((set, get) => ({
  // Start from the last known-good settings rather than from nothing, so a
  // slow or refused first fetch does not flash a different app.
  features: readCache()?.features ?? {},
  defaults: readCache()?.defaults ?? {},
  loaded: false,
  stale: false,

  load: async () => {
    for (let attempt = 0; ; attempt += 1) {
      try {
        const { features, defaults } = await getPublicSettings();
        writeCache({ features, defaults });
        set({ features, defaults, loaded: true, stale: false });
        return;
      } catch {
        const cached = readCache();
        if (cached) {
          // Keep showing what the platform last told us it was. Wrong for a
          // few seconds after a real change beats silently dropping a feature.
          set({ ...cached, loaded: true, stale: true });
        } else {
          set({ loaded: true, stale: true });
        }
        if (attempt >= RETRY_MS.length) return;
        await new Promise((r) => { setTimeout(r, RETRY_MS[attempt]); });
      }
    }
  },

  // Unknown key while loaded => the flag doesn't exist => treat as ON (the
  // backend is the gate). While still loading, also ON to avoid a flash of
  // hidden controls.
  isEnabled: (key) => {
    const { features, loaded } = get();
    if (!loaded) return true;
    return key in features ? features[key] : true;
  },
}));

// Convenience hook for components. Mirrors isEnabled(): ON while loading or for
// unknown keys; otherwise the flag's value.
export function useFeature(key: string): boolean {
  return useFeatureStore((s) =>
    !s.loaded ? true : key in s.features ? s.features[key] : true
  );
}
